import React, { useState, JSX, useEffect } from 'react';
import {
  ImageField,
  Field,
  LinkField,
  Link,
  RichText,
  RichTextField,
  useSitecoreContext,
  SitecoreContextValue,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StyleguideComponentProps } from 'lib/component-props';
import Video from 'components/atoms/Video';
import { ItemValue } from '../types/ListItem';
import Icons from '../atoms/Icons';
import Shapes from 'components/molecules/Shapes';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { setImageSizeString } from 'assets/utils/setImageSizeString';
import { convertImageUrl } from 'assets/utils/convertImageUrl';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';

type CustomSitecoreContextType = {
  sitecoreContext: SitecoreContextValue & {
    itemPath?: string;
  };
};

type HeroComponentProps = Partial<StyleguideComponentProps> & {
  fields: {
    BaseImage: ImageField | any;
    BaseVIdeoURL?: Field<string>;
    BaseTitle: Field<string>;
    BaseDescription?: RichTextField;
    BaseLink?: LinkField;
    BaseLinkType?: ItemValue;
    BaseChoiShapes?: ItemValue;
    image?: ImageField;
    videoURL?: Field<string>;
    description?: Field<string>;
    renderingVariant?: ItemValue;
    primaryCTA?: LinkField;
    secondaryCTA?: LinkField;
    dynamicHero?: Field<boolean>;
    descriptionAlignment?: Field<string>;
    descriptionColor?: ItemValue;
    BasePhoneNumber?: Field<string>;
    lowerLevelHero: {
      value: boolean;
    };
    titleAlignment?: ItemValue;
    titleColor?: ItemValue;
    'Alternate Hero'?: {
      value: boolean;
    };
    'Alt Hero h1 Gradient'?: {
      value: boolean;
    };
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
  };
};

const HeroComponent = (props: HeroComponentProps): JSX.Element => {
  if (!props || !props.fields) {
    return <div></div>;
  }
  const [playing, setPlaying] = useState(1);
  const [viewportSize, setViewportSize] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState(props.fields.BaseImage?.value?.src);
  const { sitecoreContext } = useSitecoreContext() as CustomSitecoreContextType;

  const controlVideo = () => {
    const vid: HTMLMediaElement | null = document.querySelector('.video');
    setPlaying(playing == 0 ? 1 : 0);
    playing == 0 ? vid?.play() : vid?.pause();
  };

  const baseImage = props.fields.BaseImage?.value?.src;
  const hasTitleAlignment = props.fields.titleAlignment?.displayName;
  const hasDescriptionAlignment = props.fields.descriptionAlignment?.value;
  const titleAlignment = hasTitleAlignment || 'Left';
  const descriptionAlignment = hasDescriptionAlignment || 'Left';
  const titleColor = props.fields.titleColor?.displayName || 'White';
  const descriptionColor =
    props.fields.descriptionColor?.displayName || 'White';
  const isLowerLevel = props.fields.lowerLevelHero?.value === true;
  const buttonType =
    props.fields.BaseLinkType?.name?.toLowerCase() || 'primary';
  const isAlternate = props.fields['Alternate Hero']?.value === true;
  const isAltGradient = props.fields['Alt Hero h1 Gradient']?.value === true;
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const isOncallB2C =
    !sitecoreContext.itemPath?.includes('/oncall/business-solutions') &&
    sitecoreContext.itemPath?.includes('/oncall');

  const isSisters = sitecoreContext.itemPath?.includes('/sisters');

  const heroImageSizes = isLowerLevel
    ? '(max-width: 767px) 1600px, (max-width: 1199px) 1800px, 2000px'
    : '100vw';

  const alternateHeroImageSizes =
    '(max-width: 767px) 90vw, (max-width: 1023px) 44vw, (max-width: 1439px) 32vw, (max-width: 1679px) 31vw, (min-width: 1680px) 31vw, 32vw';

  const phoneNumber = props.fields.BasePhoneNumber?.value?.trim();
  const linkText = props.fields.BaseLink?.value?.text?.trim();

  useEffect(() => {
    setViewportSize(getOsfViewportSize());
    const handleResize = () => setViewportSize(getOsfViewportSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (viewportSize) {
      setImageSize(setImageSizeString(viewportSize));
    }
  }, [viewportSize]);

  useEffect(() => {
    setImageUrl(
      imageSize ? convertImageUrl(baseImage as string, imageSize) : baseImage
    );
  }, [imageSize, baseImage]);

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`hero-${props.rendering?.uid}`}
    >
      <div
        className={classNames(
          'hero',
          props.fields.BaseVIdeoURL?.value ? 'hero-video' : '',
          isLowerLevel ? 'lower-level' : '',
          isAlternate ? 'alternate background-gradient' : ''
        )}
      >
        {props.fields?.BaseChoiShapes?.name && (
          <Shapes
            variant={props.fields?.BaseChoiShapes?.name
              .toLowerCase()
              .replaceAll(' ', '')}
          ></Shapes>
        )}
        {props.fields.BaseVIdeoURL?.value && (
          <div
            className="video-btn"
            onClick={controlVideo}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                controlVideo();
              }
            }}
            tabIndex={0}
          >
            {playing == 0 ? (
              <Icons id={'play-btn'}></Icons>
            ) : (
              <Icons id={'pause-btn'}></Icons>
            )}
          </div>
        )}
        {imageSize && baseImage && !isAlternate && (
          <img
            src={imageUrl}
            onError={() => setImageUrl(baseImage)}
            width={props.fields.BaseImage.value?.width as number}
            height={props.fields.BaseImage.value?.height as number}
            {...(!isLowerLevel ? { loading: 'eager' } : {})}
            alt={
              (props.fields.BaseImage.value?.alt as string) != ''
                ? (props.fields.BaseImage.value?.alt as string)
                : 'Image'
            }
            {...(!isLowerLevel ? { fetchPriority: 'high' } : {})}
            className="hero-img"
            sizes={heroImageSizes}
          />
        )}
        {imageSize && isAlternate && baseImage && (
          <div className="img-wrapper">
            <img
              src={imageUrl}
              onError={() => setImageUrl(baseImage)}
              width={props.fields.BaseImage.value?.width as number}
              height={props.fields.BaseImage.value?.height as number}
              {...(!isLowerLevel ? { loading: 'eager' } : {})}
              alt={
                (props.fields.BaseImage.value?.alt as string) != ''
                  ? (props.fields.BaseImage.value?.alt as string)
                  : 'Image'
              }
              {...(!isLowerLevel ? { fetchPriority: 'high' } : {})}
              className="hero-img"
              sizes={alternateHeroImageSizes}
            />
          </div>
        )}
        {props.fields.BaseVIdeoURL?.value && (
          <div className="hero-video">
            <Video
              videoUrl={props.fields?.BaseVIdeoURL?.value}
              aditionalClass="video hero-video"
            ></Video>
          </div>
        )}
        <div className="hero-inner-wrapper">
          <div className="jss-inner-container">
            <div className="hero-content">
              {props.fields.BaseTitle?.value && (
                <h1
                  className={classNames(
                    titleAlignment
                      ? `hero-title hero-title--${titleAlignment.toLowerCase()} hero-title--${titleColor.toLowerCase()} osf-display-h1`
                      : isAlternate
                        ? 'osf-display-h1'
                        : 'osf-headline-h1',
                    isAltGradient ? 'hero-title-gradient' : ''
                  )}
                >
                  {props.fields.BaseTitle?.value}
                </h1>
              )}
              {props.fields.BaseDescription?.value &&
                (descriptionAlignment ? (
                  <RichText
                    className={`hero-description hero-description--${descriptionAlignment.toLowerCase()} hero-description--${descriptionColor.toLowerCase()} osf-intro-copy`}
                    field={props.fields.BaseDescription}
                  />
                ) : (
                  <p className="osf-intro-copy">
                    {props.fields.BaseDescription?.value}
                  </p>
                ))}
              {phoneNumber ? (
                <a
                  href={`tel:${phoneNumber}`}
                  className={classNames(
                    `btn-${buttonType}`,
                    isAlternate ? 'btn-mobile-fw' : ''
                  )}
                >
                  {linkText || phoneNumber}
                </a>
              ) : (
                props.fields.BaseLink?.value?.href &&
                props.fields.BaseLink.value.href !== '' && (
                  <Link
                    className={classNames(
                      `btn-${buttonType}`,
                      isAlternate ? 'btn-mobile-fw' : ''
                    )}
                    field={props.fields.BaseLink}
                  />
                )
              )}
            </div>
          </div>
        </div>
        {isLowerLevel && (isOncallB2C || isSisters) && (
          <div
            className={classNames(
              'overhauled',
              'header-border',
              isOncallB2C ? 'gradient-horizontal' : '',
              isSisters ? 'sisters-border' : ''
            )}
          ></div>
        )}
      </div>
    </div>
  );
};

export default HeroComponent;
